import { useReadyToInvoiceJobsInvalidator } from '../invoicing/invoicingQueries'
import { useMutation, useQuery, useQueryClient } from "react-query"
import { archiveJob, archiveJobCheck, deleteJob, deleteJobCheck, deleteVisit, getJobUrl, getVisits, getVisitsGpsData, loadLegacyJobData, printJobReport, PrintJobReportRequest, unarchiveJob, updateJob, updateVisit } from "./jobsApi"
import { notification } from "antd"
import { LegacyJobData, UpdateJobRequest, UpdateVisitRequest } from "./jobs"
import { useEstimatesReportInvalidator } from "../../components/Datacenter/EstimatesReport/estimatesReportQueries"
import { useJobListInvalidator } from "../../components/JobList/jobListApi"
import { useScheduleEventsInvalidator } from "../../components/ScheduleManagement/scheduleQueries"

export const useVisitsQuery = (jobId: number) => {
  return useQuery(['visits', { jobId }], {
    queryFn: () => getVisits(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false
  })
}

export const useInvalidateVisits = () => {
  const queryClient = useQueryClient()
  return (jobId: number) => queryClient.invalidateQueries(['visits', { jobId }])
}

export const useVisitsGpsDataQuery = (visitId: string | undefined) => {
  return useQuery(['gpsData', { visitId }], {
    queryFn: () => getVisitsGpsData(visitId),
    enabled: !!visitId,
    refetchOnWindowFocus: false
  })
}

export const useDeleteVisitMutation = () => {
  const queryClient = useQueryClient()
  const scheduleEvents = useScheduleEventsInvalidator()
  return useMutation({
    mutationFn: ({ visitId, deletePayrollTime }: { visitId: string, deletePayrollTime: boolean }) => deleteVisit(visitId, deletePayrollTime),
    onSuccess: () => {
      queryClient.invalidateQueries('visits')
      scheduleEvents.invalidateAllEvents()
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'There was an error deleting the visit.'
      notification.error({ message })
    }
  })
}

export const useUpdateVisitMutation = () => {
  const queryClient = useQueryClient()
  const invalidateInvoiceJobs = useReadyToInvoiceJobsInvalidator()
  return useMutation({
    mutationFn: (request: UpdateVisitRequest) => updateVisit(request),
    onSuccess: () => {
      queryClient.invalidateQueries('visits')
      invalidateInvoiceJobs()
    },
    onError: () => { notification.error({ message: 'There was an error updating the visit.' }) }
  })
}

export const useDeleteJobCheckQuery = (jobId: number) => {
  return useQuery(['delete-job-check', { jobId }], {
    queryFn: () => deleteJobCheck(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false
  })
}

export const useDeleteJobMutation = () => {
  return useMutation({
    mutationFn: (jobId: number) => deleteJob(jobId),
    onError: () => { notification.error({ message: 'There was an error deleting the job.' }) }
  })
}

export const useArchiveJobCheckQuery = (jobId: number) => {
  return useQuery(['archive-job-check', { jobId }], {
    queryFn: () => archiveJobCheck(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false
  })
}

export const useArchiveJobMutation = () => {
  const queryClient = useQueryClient()
  const jobListInvalidator = useJobListInvalidator()
  const estimatesReportInvalidator = useEstimatesReportInvalidator()
  return useMutation({
    mutationFn: (jobId: number) => archiveJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries(['archive-job-check', { jobId }])
      queryClient.invalidateQueries(['legacy-job-data', { jobId }])
      jobListInvalidator.invalidate()
      estimatesReportInvalidator.invalidate()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message ?? 'There was an error archiving the job.'
      notification.error({ message })
    }
  })
}

export const useUnarchiveJobMutation = () => {
  const queryClient = useQueryClient()
  const jobListInvalidator = useJobListInvalidator()
  const estimatesReportInvalidator = useEstimatesReportInvalidator()
  return useMutation({
    mutationFn: (jobId: number) => unarchiveJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries(['archive-job-check', { jobId }])
      queryClient.invalidateQueries(['legacy-job-data', { jobId }])
      jobListInvalidator.invalidate()
      estimatesReportInvalidator.invalidate()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message ?? 'There was an error unarchiving the job.'
      notification.error({ message })
    }
  })
}

export const useGetJobUrlQuery = (jobId: number) => {
  return useQuery(['job-url', { jobId }], {
    queryFn: () => getJobUrl(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false
  })
}

export const useLegacyJobDataQuery = (jobId: number) => {
  return useQuery(['legacy-job-data', { jobId }], {
    queryFn: () => loadLegacyJobData(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false
  })
}

export const usePrintJobReportMutation = (onSuccess: (url: string) => void) => {
  return useMutation({
    mutationFn: (request: PrintJobReportRequest) => printJobReport(request),
    onSuccess,
    onError: () => {
      notification.error({ message: 'There was an error printing.' })
    }
  })
}

export const useUpdateJobMutation = (onSuccess: (job: LegacyJobData) => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: UpdateJobRequest) => updateJob(request),
    onSuccess: (job) => {
      queryClient.setQueryData(['legacy-job-data', { jobId: job.id }], job)
      queryClient.invalidateQueries(['jobDetails', job.id])
      queryClient.invalidateQueries(['workareaGroups', { jobId: job.id }])
      onSuccess(job)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message ?? 'There was an error saving this job.'
      notification.error({ message })
    }
  })
}

export const useLegacyJobDataInvalidator = () => {
  const client = useQueryClient()
  return { invalidateForJob: (jobId: number) => client.invalidateQueries(['legacy-job-data', { jobId }]) }
}
